/**
 * structuredData.ts — Datos estructurados Schema.org centralizados (GEO/SEO).
 *
 * Estos builders generan JSON-LD que se inyecta vía el componente <SEO />.
 * Objetivo GEO: que buscadores y agentes IA entiendan y citen correctamente
 * el negocio (préstamo P2P de sets LEGO entre particulares), el sistema de
 * brickpoints y las FAQ.
 */

const SITE = 'https://www.brickshare.org';
const LOGO = `${SITE}/og-image.png`;

/**
 * Organization — identidad de marca. Incluye `sameAs` (redes sociales) para
 * que los motores generativos asocien correctamente las cuentas oficiales.
 */
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Brickshare',
  legalName: 'Infinigames Lab SL',
  url: SITE,
  logo: LOGO,
  email: 'info@brickshare.org',
  description:
    'Plataforma española de préstamo de sets LEGO® entre particulares: cada ' +
    'miembro comparte sus sets con la comunidad. ' +
    'Economía circular con impacto social positivo.',
  areaServed: { '@type': 'Country', name: 'España' },
  sameAs: [
    'https://www.instagram.com/brickshare_es',
    'https://www.facebook.com/brickshare',
    'https://twitter.com/brickshare_es',
  ],
} as const;

/**
 * WebSite — habilita el SearchAction (búsqueda del catálogo) para rich results.
 */
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Brickshare',
  url: SITE,
  inLanguage: 'es-ES',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE}/catalogo?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
} as const;

/**
 * Service — describe el servicio de préstamo P2P de sets LEGO.
 */
export function buildServiceSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Préstamo P2P de sets LEGO® entre particulares',
    provider: { '@type': 'Organization', name: 'Brickshare', url: SITE },
    areaServed: { '@type': 'Country', name: 'España' },
    description:
      'Servicio de alquiler temporal e intercambio de sets LEGO® entre particulares: ' +
      'cada miembro comparte sus sets para que otros miembros los disfruten temporalmente, ' +
      'con envíos seguros gestionados punto a punto.',
  };
}

import { getAllFaqItems } from '@/data/faqData';

/**
 * FAQPage — preguntas frecuentes del modelo P2P y operativa de propietarios.
 */
export function buildFaqSchema(customFaqs?: { q: string; a: string }[]) {
  const faqs = customFaqs ?? getAllFaqItems().map((item) => ({ q: item.q, a: item.a }));

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

/**
 * BreadcrumbList — migas de pan para una ruta dada.
 * @param items pares [nombre, path] en orden jerárquico. El path es relativo
 *              a la raíz del sitio (p. ej. "/catalogo").
 */
export function buildBreadcrumbSchema(items: [string, string][]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map(([name, pathname], i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name,
      item: `${SITE}${pathname}`,
    })),
  };
}

/**
 * Product — ficha de un set individual del catálogo P2P.
 */
export function buildProductSchema(set: {
  name: string;
  description?: string | null;
  image?: string | null;
  ref?: string | null;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: set.name,
    ...(set.description ? { description: set.description } : {}),
    ...(set.image ? { image: set.image } : {}),
    ...(set.ref ? { sku: set.ref, mpn: set.ref } : {}),
    brand: { '@type': 'Brand', name: 'LEGO' },
  };
}
